// src/pages/admin/promotions/EditPromotion.jsx
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import { 
  ArrowLeft, 
  Save, 
  RefreshCw,
  Plus, 
  X, 
  Search,
  CheckCircle,
  XCircle,
  Package,
  Tag,
  Shield,
  ChevronDown
} from 'lucide-react'
import PromotionService from '../../../services/promotionService'
import CustomerGroupService from '../../../services/customerGroupService'
import ProductService from '../../../services/productService'
import CategoryService from '../../../services/categoryService'
import BranchService from '../../../services/branchService'
import { PROMOTION_TYPE_CONFIG, PRIORITY_CONFIG } from '../../../utils/promotionModel'
import { formatPromotionForAPI, validatePromotionForm } from '../../../utils/promotionUtils'
import PageLoader from '../../../components/ui/PageLoader'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const EditPromotion = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    promotionType: '',
    discountType: '',
    discountValue: '',
    priority: 1,
    targetAudience: '',
    startDate: '',
    endDate: '',
    products: [],
    conditions: [],
    isActive: true,
    branches: []
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)

  // Dropdown data
  const [customerGroups, setCustomerGroups] = useState([])
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])

  // Loading states for dropdowns
  const [dropdownLoading, setDropdownLoading] = useState({
    customerGroups: true,
    products: true,
    categories: true,
    branches: true
  })

  // Product selection modal
  const [showProductModal, setShowProductModal] = useState(false)
  const [productSearchTerm, setProductSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  // Access control - only admins can edit promotions
  const { hasPermission } = usePermissions()
  const currentUser = AuthService.getCurrentUser()
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin'
  const canEditPromotions = hasPermission('promotions.edit') || isAdmin

  // Redirect non-authorized users
  useEffect(() => {
    if (!canEditPromotions) {
      console.warn('🚫 Unauthorized access attempt to Edit Promotion')
      navigate('/admin/promotions/all')
      return
    }
  }, [canEditPromotions, navigate])

  // Initialize component
  useEffect(() => {
    if (id) {
      fetchPromotionData()
      fetchDropdownData()
    }
  }, [id])

  // Debug useEffect for customer groups
  useEffect(() => {
    console.log('🔍 Customer Groups State Updated:', {
      length: customerGroups.length,
      groups: customerGroups,
      isArray: Array.isArray(customerGroups),
      loading: dropdownLoading.customerGroups
    })
  }, [customerGroups, dropdownLoading.customerGroups])

  // If user doesn't have permission, show access denied
  if (!canEditPromotions) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-8 text-center">
            <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600 mb-4">
              You don't have permission to edit promotions. Only administrators can edit promotions.
            </p>
            <Button
              onClick={() => navigate('/admin/promotions/all')}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Back to Promotions
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // ENHANCED: Enhanced data extraction function (from CreatePromotion)
  const extractCustomerGroupsData = (response) => {
    console.log('🔍 Extracting customer groups from response:', response)
    
    if (!response || !response.success) {
      console.log('❌ Response not successful or missing')
      return []
    }

    let groups = []

    // Handle different response structures based on your API patterns
    if (response.data?.customerGroups) {
      groups = response.data.customerGroups
      console.log('✅ Found groups in data.customerGroups')
    } else if (response.data?.data?.customerGroups) {
      groups = response.data.data.customerGroups
      console.log('✅ Found groups in data.data.customerGroups')
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      groups = response.data.data
      console.log('✅ Found groups in data.data array')
    } else if (Array.isArray(response.data)) {
      groups = response.data
      console.log('✅ Found groups in data array')
    } else if (response.customerGroups) {
      groups = response.customerGroups
      console.log('✅ Found groups in customerGroups')
    } else {
      // Try to find any array in the response that looks like customer groups
      const possibleArrays = Object.values(response.data || {}).filter(Array.isArray)
      if (possibleArrays.length > 0) {
        groups = possibleArrays[0]
        console.log('✅ Found groups in first available array')
      }
    }

    console.log('🔍 Raw extracted groups:', groups)

    // Validate and filter groups
    const validGroups = Array.isArray(groups) 
      ? groups.filter(group => {
          if (!group || typeof group !== 'object') {
            console.log('❌ Invalid group (not object):', group)
            return false
          }
          if (!group._id) {
            console.log('❌ Invalid group (no _id):', group)
            return false
          }
          if (!group.name) {
            console.log('❌ Invalid group (no name):', group)
            return false
          }
          // Only include active groups (assume active if isActive is not explicitly false)
          if (group.isActive === false) {
            console.log('❌ Inactive group filtered out:', group.name)
            return false
          }
          console.log('✅ Valid group:', group.name)
          return true
        })
      : []

    console.log('🎯 Final valid groups:', validGroups)
    return validGroups
  }

  // Fetch existing promotion data
  const fetchPromotionData = async () => {
    try {
      setLoading(true)
      console.log('🎉 Fetching promotion data for ID:', id)

      const response = await PromotionService.getPromotionById(id)
      
      if (response && response.success) {
        const promotion = response.data.promotion || response.data
        console.log('✅ Promotion data fetched:', promotion)

        // Format data for form
        const formattedData = {
          name: promotion.name || '',
          description: promotion.description || '',
          promotionType: promotion.promotionType || '',
          discountType: promotion.discountType || '',
          discountValue: promotion.discountValue || '',
          priority: promotion.priority || 1,
          targetAudience: typeof promotion.targetAudience === 'object' 
            ? promotion.targetAudience._id 
            : promotion.targetAudience || '',
          startDate: promotion.startDate ? promotion.startDate.split('T')[0] : '',
          endDate: promotion.endDate ? promotion.endDate.split('T')[0] : '',
          products: promotion.products?.map(p => typeof p === 'object' ? p._id : p) || [],
          conditions: promotion.conditions || [],
          isActive: promotion.isActive !== undefined ? promotion.isActive : true,
          branches: promotion.branches?.map(b => typeof b === 'object' ? b._id : b) || []
        }

        setFormData(formattedData)
        setSelectedProducts(promotion.products || [])
        
        showToast('Promotion data loaded successfully')
      } else {
        throw new Error(response?.message || 'Failed to fetch promotion data')
      }
    } catch (error) {
      console.error('❌ Error fetching promotion:', error)
      showToast(error.message || 'Failed to load promotion data', 'error')
      navigate('/admin/promotions/all') // Redirect back if promotion not found
    } finally {
      setLoading(false)
    }
  }

  // ENHANCED: Fetch dropdown data with enhanced error handling (from CreatePromotion)
  const fetchDropdownData = async () => {
    try {
      console.log('📦 Fetching dropdown data...')

      // Set all loading states
      setDropdownLoading({
        customerGroups: true,
        products: true,
        categories: true,
        branches: true
      })

      const [customerGroupsRes, productsRes, categoriesRes, branchesRes] = await Promise.all([
        CustomerGroupService.getAllCustomerGroups().catch(err => {
          console.warn('Customer groups fetch failed:', err)
          return { success: false, data: [], error: err.message }
        }),
        ProductService.getAllProducts().catch(err => {
          console.warn('Products fetch failed:', err)
          return { success: false, data: [], error: err.message }
        }),
        CategoryService.getAllCategories().catch(err => {
          console.warn('Categories fetch failed:', err)
          return { success: false, data: [], error: err.message }
        }),
        BranchService.getAllBranches().catch(err => {
          console.warn('Branches fetch failed:', err)
          return { success: false, data: [], error: err.message }
        })
      ])

      // ENHANCED: Process customer groups with enhanced extraction
      console.log('🔍 Raw Customer Groups Response:', customerGroupsRes)
      const extractedGroups = extractCustomerGroupsData(customerGroupsRes)
      setCustomerGroups(extractedGroups)
      
      if (extractedGroups.length === 0 && customerGroupsRes.success) {
        console.warn('⚠️ No valid customer groups found despite successful API call')
        showToast('No active customer groups found', 'warning')
      } else if (extractedGroups.length > 0) {
        console.log(`✅ Successfully loaded ${extractedGroups.length} customer groups`)
        showToast(`Loaded ${extractedGroups.length} customer groups`, 'success')
      }

      // Set products with enhanced handling
      if (productsRes?.success) {
        let productsList = []
        
        if (productsRes.data?.products) {
          productsList = productsRes.data.products
        } else if (Array.isArray(productsRes.data)) {
          productsList = productsRes.data
        } else if (productsRes.data) {
          productsList = productsRes.data
        }
        
        const validProducts = Array.isArray(productsList)
          ? productsList.filter(product => product && product._id) // keep ALL products, show inactive in red
          : []
        
        setProducts(validProducts)
        console.log('✅ Products loaded:', validProducts.length)
      } else {
        setProducts([])
        console.log('⚠️ Products fetch failed:', productsRes.error)
      }

      // Set categories with enhanced handling
      if (categoriesRes?.success) {
        let categoriesList = []
        
        if (categoriesRes.data?.categories) {
          categoriesList = categoriesRes.data.categories
        } else if (Array.isArray(categoriesRes.data)) {
          categoriesList = categoriesRes.data
        } else if (categoriesRes.data) {
          categoriesList = categoriesRes.data
        }
        
        const validCategories = Array.isArray(categoriesList) 
          ? categoriesList.filter(category => category && category._id && category.isActive !== false)
          : []
        
        setCategories(validCategories)
        console.log('✅ Categories loaded:', validCategories.length)
      } else {
        setCategories([])
        console.log('⚠️ Categories fetch failed:', categoriesRes.error)
      }

      // Set branches with enhanced handling
      if (branchesRes?.success) {
        let branchesList = []
        if (branchesRes.data?.branches) {
          branchesList = branchesRes.data.branches
        } else if (branchesRes.branches) {
          branchesList = branchesRes.branches
        } else if (Array.isArray(branchesRes.data)) {
          branchesList = branchesRes.data
        } else if (Array.isArray(branchesRes)) {
          branchesList = branchesRes
        }
        
        const validBranches = Array.isArray(branchesList)
          ? branchesList.filter(branch => branch && branch._id && branch.isActive !== false)
          : []
        
        setBranches(validBranches)
        console.log('✅ Branches loaded:', validBranches.length)
      } else {
        setBranches([])
        console.log('⚠️ Branches fetch failed:', branchesRes?.error)
      }

    } catch (error) {
      console.error('❌ Error fetching dropdown data:', error)
      showToast('Failed to load dropdown data', 'error')
      
      // Set empty arrays as fallback
      setCustomerGroups([])
      setProducts([])
      setCategories([])
    } finally {
      // Clear all loading states
      setDropdownLoading({
        customerGroups: false,
        products: false,
        categories: false,
        branches: false
      })
    }
  }

  // NEW: Retry function for customer groups specifically (from CreatePromotion)
  const retryFetchCustomerGroups = async () => {
    try {
      setDropdownLoading(prev => ({ ...prev, customerGroups: true }))
      console.log('🔄 Retrying customer groups fetch...')
      
      const response = await CustomerGroupService.getAllCustomerGroups()
      console.log('🔍 Retry Response:', response)
      
      const extractedGroups = extractCustomerGroupsData(response)
      setCustomerGroups(extractedGroups)
      
      if (extractedGroups.length > 0) {
        showToast(`Successfully loaded ${extractedGroups.length} customer groups!`)
        console.log(`✅ Retry successful: ${extractedGroups.length} groups loaded`)
      } else {
        throw new Error('No valid customer groups found in response')
      }
    } catch (error) {
      console.error('❌ Retry failed:', error)
      showToast('Failed to load customer groups. Please contact support.', 'error')
      setCustomerGroups([])
    } finally {
      setDropdownLoading(prev => ({ ...prev, customerGroups: false }))
    }
  }

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  // Handle product selection
  const handleProductSelection = (productIds) => {
    // Ensure productIds is an array
    const validProductIds = Array.isArray(productIds) ? productIds : []
    
    setFormData(prev => ({
      ...prev,
      products: validProductIds
    }))

    // Update selected products for display
    if (Array.isArray(products) && products.length > 0) {
      const selected = products.filter(product => 
        product && product._id && validProductIds.includes(product._id)
      )
      setSelectedProducts(selected)
    } else {
      setSelectedProducts([])
    }
  }

  // Add condition
  const addCondition = (conditionType) => {
    const newCondition = getDefaultCondition(conditionType)
    
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }))
  }

  // Update condition
  const updateCondition = (index, updatedCondition) => {
    if (index < 0 || !updatedCondition) return
    
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) => 
        i === index ? { ...condition, ...updatedCondition } : condition
      )
    }))
  }

  // Remove condition
  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }))
  }

  // Get default condition structure
  const getDefaultCondition = (type) => {
    const defaults = {
      'days_active': {
        type: 'days_active',
        daysActive: []
      },
      'time_range': {
        type: 'time_range',
        timeRange: {
          startTime: '09:00',
          endTime: '17:00'
        }
      },
      'min_quantity': {
        type: 'min_quantity',
        minQuantity: 1
      },
      'max_uses_per_order': {
        type: 'max_uses_per_order',
        maxUsesPerOrder: 1
      },
      'min_order_value': {
        type: 'min_order_value',
        minOrderValue: 0
      },
      'payment_method': {
        type: 'payment_method',
        paymentMethods: []
      }
    }

    return defaults[type] || { type }
  }

  // Get available conditions for current promotion type
  const getAvailableConditions = () => {
    const allConditions = [
      { value: 'days_active', label: 'Days Active' },
      { value: 'time_range', label: 'Time Range' },
      { value: 'min_quantity', label: 'Minimum Quantity' },
      { value: 'max_uses_per_order', label: 'Max Uses Per Order' },
      { value: 'min_order_value', label: 'Minimum Order Value' },
      { value: 'payment_method', label: 'Payment Method' }
    ]

    // Filter based on promotion type
    switch (formData.promotionType) {
      case 'product_based':
        return allConditions.filter(c => 
          ['days_active', 'time_range', 'min_quantity', 'max_uses_per_order'].includes(c.value)
        )
      case 'order_based':
        return allConditions.filter(c => 
          ['days_active', 'time_range', 'min_order_value', 'max_uses_per_order'].includes(c.value)
        )
      default:
        return allConditions
    }
  }

  // Filter products for modal
  const getFilteredProducts = () => {
    if (!Array.isArray(products)) {
      return []
    }

    let filtered = [...products]

    if (productSearchTerm && productSearchTerm.trim()) {
      const searchLower = productSearchTerm.toLowerCase().trim()
      filtered = filtered.filter(product => {
        if (!product) return false
        
        const name = product.name?.toLowerCase() || ''
        const sku = product.sku?.toLowerCase() || ''
        
        return name.includes(searchLower) || sku.includes(searchLower)
      })
    }

    if (selectedCategory) {
      filtered = filtered.filter(product => {
        if (!product) return false
        
        return product.category === selectedCategory ||
               product.categoryDetails?._id === selectedCategory
      })
    }

    return filtered
  }

  // NEW: Render customer groups options (from CreatePromotion)
  const renderCustomerGroupOptions = () => {
    if (dropdownLoading.customerGroups) {
      return <option value="" disabled>Loading customer groups...</option>
    }
    
    if (!Array.isArray(customerGroups)) {
      return <option value="" disabled>Error loading customer groups</option>
    }
    
    if (customerGroups.length === 0) {
      return (
        <>
          <option value="" disabled>No customer groups available</option>
          <option value="retry" style={{ color: 'orange' }}>🔄 Click to retry loading</option>
        </>
      )
    }
    
    return customerGroups.map(group => {
      if (!group || !group._id) {
        console.warn('⚠️ Invalid group found:', group)
        return null
      }
      
      return (
        <option key={group._id} value={group._id}>
          👥 {group.name || 'Unnamed Group'}
        </option>
      )
    }).filter(Boolean)
  }

  // NEW: Handle target audience change with retry logic (from CreatePromotion)
  const handleTargetAudienceChange = (e) => {
    if (e.target.value === 'retry') {
      retryFetchCustomerGroups()
      return
    }
    
    handleInputChange(e)
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setSaving(true)
      setErrors({})

      // Validate form data
      const validation = validatePromotionForm(formData)
      if (!validation.isValid) {
        setErrors(validation.errors || {})
        showToast('Please fix the validation errors', 'error')
        return
      }

      // Format data for API
      let apiData
      try {
        apiData = formatPromotionForAPI(formData)
      } catch (formatError) {
        console.error('❌ Error formatting data:', formatError)
        showToast('Invalid form data. Please check your inputs.', 'error')
        return
      }
      
      console.log('🚀 Updating promotion with data:', apiData)

      // Call update API
      const response = await PromotionService.updatePromotion(id, apiData)

      if (response && response.success) {
        console.log('✅ Promotion updated successfully:', response)
        showToast('Promotion updated successfully!')
        
        // Navigate back to list after short delay
        setTimeout(() => {
          navigate('/admin/promotions/all')
        }, 1500)
      } else {
        throw new Error(response?.message || 'Failed to update promotion')
      }
    } catch (error) {
      console.error('❌ Error updating promotion:', error)
      
      // Handle specific error types
      if (error.response?.status === 400 || error.response?.status === 422) {
        const backendErrors = error.response.data?.errors || {}
        setErrors(backendErrors)
        showToast(error.response.data?.message || 'Validation failed', 'error')
      } else if (error.response?.status === 404) {
        showToast('Promotion not found', 'error')
        navigate('/admin/promotions/all')
      } else if (error.response?.status === 409) {
        showToast(error.response.data?.message || 'Promotion name already exists', 'error')
      } else if (error.response?.status === 401) {
        showToast('Authentication required. Please login again.', 'error')
      } else if (error.message?.includes('Network Error')) {
        showToast('Network error. Please check your connection.', 'error')
      } else {
        showToast(error.message || 'Failed to update promotion', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <PageLoader 
        showHeader={false}
        title="Loading promotion data..."
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/promotions/all')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Promotion</h1>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Left Column - Basic Information */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Promotion Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Promotion Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter promotion name"
                      className={errors.name ? 'border-red-500' : ''}
                      required
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Promotion Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Promotion Type *
                    </label>
                    <div className="relative">
                      <select
                        name="promotionType"
                        value={formData.promotionType}
                        onChange={handleInputChange}
                        disabled
                        className={`w-full h-11 px-3 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-gray-50 cursor-not-allowed ${
                          errors.promotionType ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="">Select promotion type</option>
                        {Object.values(PROMOTION_TYPE_CONFIG).map(type => (
                          <option key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                    {errors.promotionType && <p className="text-red-500 text-sm mt-1">{errors.promotionType}</p>}
                  </div>

                  {/* Discount Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Type *
                    </label>
                    <select
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleInputChange}
                      className={`w-full h-11 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        errors.discountType ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Select discount type</option>
                      <option value="fixed">💵 Fixed Amount</option>
                      <option value="percentage">📊 Percentage</option>
                    </select>
                    {errors.discountType && <p className="text-red-500 text-sm mt-1">{errors.discountType}</p>}
                  </div>

                  {/* Discount Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Value *
                    </label>
                    <Input
                      type="number"
                      name="discountValue"
                      value={formData.discountValue}
                      onChange={handleInputChange}
                      placeholder={formData.discountType === 'percentage' ? 'Enter percentage (0-100)' : 'Enter amount'}
                      min="0"
                      max={formData.discountType === 'percentage' ? '100' : undefined}
                      step="0.01"
                      className={errors.discountValue ? 'border-red-500' : ''}
                      required
                    />
                    {errors.discountValue && <p className="text-red-500 text-sm mt-1">{errors.discountValue}</p>}
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="w-full h-11 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {Object.values(PRIORITY_CONFIG).map(priority => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Target Audience - ENHANCED */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Audience *
                      {dropdownLoading.customerGroups && (
                        <RefreshCw className="h-4 w-4 animate-spin inline ml-2" />
                      )}
                    </label>
                    <select
                      name="targetAudience"
                      value={formData.targetAudience}
                      onChange={handleTargetAudienceChange}
                      className={`w-full h-11 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        errors.targetAudience ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                      disabled={dropdownLoading.customerGroups}
                    >
                      <option value="">Select customer group</option>
                      {renderCustomerGroupOptions()}
                    </select>
                    {errors.targetAudience && <p className="text-red-500 text-sm mt-1">{errors.targetAudience}</p>}
                    
                    {/* Debug info and status */}
                    <div className="text-xs text-gray-400 mt-1">
                      {dropdownLoading.customerGroups ? (
                        'Loading customer groups...'
                      ) : customerGroups.length > 0 ? (
                        `✅ ${customerGroups.length} customer groups loaded`
                      ) : (
                        <span className="text-orange-600">
                          ⚠️ No customer groups found
                          <button
                            type="button"
                            onClick={retryFetchCustomerGroups}
                            className="ml-2 text-orange-500 hover:text-orange-600 underline"
                          >
                            🔄 Retry
                          </button>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Branches Selection */}
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Applicable Branches *
                      {dropdownLoading.branches && (
                        <RefreshCw className="h-4 w-4 animate-spin inline ml-2" />
                      )}
                    </label>
                    <div className="space-y-3">
                      <div className="flex gap-4">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="applyToAllBranches"
                            checked={!formData.branches || formData.branches.length === 0}
                            onChange={() => setFormData(prev => ({ ...prev, branches: [] }))}
                            className="form-radio text-orange-500 focus:ring-orange-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">All Branches</span>
                        </label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="applyToAllBranches"
                            checked={formData.branches && formData.branches.length > 0}
                            onChange={() => {
                              if ((!formData.branches || formData.branches.length === 0) && branches.length > 0) {
                                setFormData(prev => ({ ...prev, branches: [branches[0]._id] }));
                              }
                            }}
                            className="form-radio text-orange-500 focus:ring-orange-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Specific Branches</span>
                        </label>
                      </div>

                      {formData.branches && formData.branches.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-500 mb-2.5">Select one or more branches where this promotion is active:</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {branches.map(branch => {
                              const isChecked = formData.branches.includes(branch._id);
                              return (
                                <label key={branch._id} className="inline-flex items-center bg-white p-2 rounded border border-gray-200 hover:bg-orange-50/20 hover:border-orange-200 transition-all cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      let updatedBranches;
                                      if (isChecked) {
                                        updatedBranches = formData.branches.filter(id => id !== branch._id);
                                      } else {
                                        updatedBranches = [...formData.branches, branch._id];
                                      }
                                      setFormData(prev => ({ ...prev, branches: updatedBranches }));
                                    }}
                                    className="form-checkbox h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-xs font-medium text-gray-700 select-none">{branch.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Enter promotion description"
                    />
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-1 gap-6">
                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date *
                      </label>
                      <Input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        className={errors.startDate ? 'border-red-500' : ''}
                        required
                      />
                      {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                    </div>

                    {/* End Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date *
                      </label>
                      <Input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        className={errors.endDate ? 'border-red-500' : ''}
                        required
                      />
                      {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                    </div>
                  </div>

                  {/* Active Status - Switch Toggle */}
                  <div className="flex items-center justify-between">
                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                      Active Status
                    </label>
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="isActive"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <label
                        htmlFor="isActive"
                        className={`relative inline-flex items-center cursor-pointer w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                          formData.isActive ? 'bg-orange-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out ${
                            formData.isActive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Products and Conditions */}
            <div className="space-y-6">
              
              {/* Products Section (for product_based promotions) */}
              {formData.promotionType === 'product_based' && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Products</CardTitle>
                      <Button
                        type="button"
                        onClick={() => setShowProductModal(true)}
                        variant="outline"
                        className="border-orange-500 text-orange-500 hover:bg-orange-50"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Products
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                  {/* Inactive products warning banner */}
                    {selectedProducts.some(p => p && p.isActive === false) && (
                      <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                        <span className="mt-0.5 text-red-500 text-sm">⚠️</span>
                        <div>
                          <p className="text-sm font-semibold text-red-700">Inactive products in promotion</p>
                          <p className="text-xs text-red-600 mt-0.5">Remove the items shown in red before saving — the backend will reject them.</p>
                        </div>
                      </div>
                    )}
                    {selectedProducts.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p>No products selected</p>
                        <p className="text-sm">Click "Add Products" to select products for this promotion</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedProducts.map(product => {
                          if (!product || !product._id) return null
                          const isInactive = product.isActive === false

                          return (
                            <div
                              key={product._id}
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                isInactive
                                  ? 'bg-red-50 border-red-300'
                                  : 'bg-gray-50 border-transparent'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <Package className={`h-5 w-5 ${isInactive ? 'text-red-400' : 'text-gray-400'}`} />
                                <div>
                                  <div className={`font-medium flex items-center gap-2 ${
                                    isInactive ? 'text-red-700' : 'text-gray-900'
                                  }`}>
                                    {product.name || 'Unnamed Product'}
                                    {isInactive && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-sm ${isInactive ? 'text-red-500' : 'text-gray-500'}`}>
                                    {product.sku || 'No SKU'}
                                    {isInactive && ' — Remove to save'}
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newProducts = formData.products.filter(id => id !== product._id)
                                  handleProductSelection(newProducts)
                                }}
                                className={`p-1 rounded transition-colors ${
                                  isInactive
                                    ? 'text-red-500 hover:text-red-700 hover:bg-red-100'
                                    : 'text-red-400 hover:text-red-600'
                                }`}
                                title={isInactive ? 'Remove inactive product' : 'Remove product'}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )
                        }).filter(Boolean)}
                      </div>
                    )}
                    {errors.products && <p className="text-red-500 text-sm mt-2">{errors.products}</p>}
                  </CardContent>
                </Card>
              )}

              {/* Conditions Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Conditions</CardTitle>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          addCondition(e.target.value)
                          e.target.value = ''
                        }
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      defaultValue=""
                    >
                      <option value="">Add Condition</option>
                      {getAvailableConditions().map(condition => (
                        <option key={condition.value} value={condition.value}>
                          {condition.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </CardHeader>
                <CardContent>
                  {formData.conditions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Tag className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>No conditions added</p>
                      <p className="text-sm">Add conditions to control when and how this promotion applies</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.conditions.map((condition, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium text-gray-700">
                              {getAvailableConditions().find(c => c.value === condition.type)?.label || condition.type}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeCondition(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Condition-specific fields */}
                          {condition.type === 'days_active' && (
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Select Days:</label>
                              <div className="grid grid-cols-7 gap-2">
                                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                  <label key={day} className="flex items-center text-sm">
                                    <input
                                      type="checkbox"
                                      checked={condition.daysActive?.includes(day) || false}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked
                                        const currentDays = condition.daysActive || []
                                        const newDays = isChecked
                                          ? [...currentDays, day]
                                          : currentDays.filter(d => d !== day)
                                        
                                        updateCondition(index, {
                                          ...condition,
                                          daysActive: newDays
                                        })
                                      }}
                                      className="mr-1"
                                    />
                                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {condition.type === 'time_range' && (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Start Time:</label>
                                <input
                                  type="time"
                                  value={condition.timeRange?.startTime || ''}
                                  onChange={(e) => updateCondition(index, {
                                    ...condition,
                                    timeRange: {
                                      ...condition.timeRange,
                                      startTime: e.target.value
                                    }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">End Time:</label>
                                <input
                                  type="time"
                                  value={condition.timeRange?.endTime || ''}
                                  onChange={(e) => updateCondition(index, {
                                    ...condition,
                                    timeRange: {
                                      ...condition.timeRange,
                                      endTime: e.target.value
                                    }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            </div>
                          )}

                          {condition.type === 'min_quantity' && (
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Minimum Quantity:</label>
                              <input
                                type="number"
                                value={condition.minQuantity || ''}
                                onChange={(e) => updateCondition(index, {
                                  ...condition,
                                  minQuantity: parseInt(e.target.value) || 0
                                })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                min="1"
                              />
                            </div>
                          )}

                          {condition.type === 'max_uses_per_order' && (
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Max Uses Per Order:</label>
                              <input
                                type="number"
                                value={condition.maxUsesPerOrder || ''}
                                onChange={(e) => updateCondition(index, {
                                  ...condition,
                                  maxUsesPerOrder: parseInt(e.target.value) || 0
                                })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                min="1"
                              />
                            </div>
                          )}

                          {condition.type === 'min_order_value' && (
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Minimum Order Value:</label>
                              <input
                                type="number"
                                value={condition.minOrderValue || ''}
                                onChange={(e) => updateCondition(index, {
                                  ...condition,
                                  minOrderValue: parseFloat(e.target.value) || 0
                                })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          )}

                          {condition.type === 'payment_method' && (
                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Payment Methods:</label>
                              <div className="flex gap-4">
                                {['cash', 'card', 'cheque'].map(method => (
                                  <label key={method} className="flex items-center text-sm">
                                    <input
                                      type="checkbox"
                                      checked={condition.paymentMethods?.includes(method) || false}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked
                                        const currentMethods = condition.paymentMethods || []
                                        const newMethods = isChecked
                                          ? [...currentMethods, method]
                                          : currentMethods.filter(m => m !== method)
                                        
                                        updateCondition(index, {
                                          ...condition,
                                          paymentMethods: newMethods
                                        })
                                      }}
                                      className="mr-1"
                                    />
                                    {method.toUpperCase()}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={() => navigate('/admin/promotions/all')}
              variant="outline"
              className="border-gray-300"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={saving || selectedProducts.some(p => p && p.isActive === false)}
              title={selectedProducts.some(p => p && p.isActive === false) ? 'Remove inactive products before saving' : ''}
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Update Promotion
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Product Selection Modal */}
        {showProductModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowProductModal(false)}
              />

              {/* Modal */}
              <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">Select Products</h3>
                  <button
                    onClick={() => setShowProductModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Filters */}
                <div className="p-6 border-b bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    {/* Category Filter */}
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">All Categories</option>
                      {Array.isArray(categories) ? categories.map(category => {
                        if (!category || !category._id) return null
                        
                        return (
                          <option key={category._id} value={category._id}>
                            {category.name || 'Unnamed Category'}
                          </option>
                        )
                      }).filter(Boolean) : null}
                    </select>
                  </div>
                </div>

                {/* Product List */}
                <div className="max-h-96 overflow-y-auto p-6">
                  {getFilteredProducts().length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>No products found</p>
                      {dropdownLoading.products && <p className="text-sm">Loading products...</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {getFilteredProducts().map(product => {
                        if (!product || !product._id) return null
                        
                        return (
                          <label key={product._id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.products.includes(product._id)}
                              onChange={(e) => {
                                const isChecked = e.target.checked
                                const currentProducts = [...formData.products]
                                
                                if (isChecked) {
                                  handleProductSelection([...currentProducts, product._id])
                                } else {
                                  handleProductSelection(currentProducts.filter(id => id !== product._id))
                                }
                              }}
                              className="mr-3"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{product.name || 'Unnamed Product'}</div>
                              <div className="text-sm text-gray-500">
                                {product.sku || 'No SKU'} • {product.categoryDetails?.name || 'No category'}
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              Rs {product.outletPrice?.toLocaleString() || '0'}
                            </div>
                          </label>
                        )
                      }).filter(Boolean)}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                  <div className="text-sm text-gray-600">
                    {formData.products.length} products selected
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      onClick={() => setShowProductModal(false)}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowProductModal(false)}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditPromotion